import { forwardRef, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, CircleCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { Segmented } from '@/components/ui/segmented';
import { StatusPill } from '@/components/ui/status-pill';
import { formatDays, formatNumber } from '@/lib/format';
import { TONE_TEXT_CLASS } from '@/lib/status';
import { cn } from '@/lib/utils';
import {
  availableCategories, countByCategory, emptyCategoryText, hiddenSummary, pickAllView,
} from '@/components/entry/needs';

const DESKTOP_ROWS = 9;
const PHONE_ROWS = 4;

function SubParts({ parts }) {
  const visible = (parts || []).filter((part) => part !== null && part !== undefined && part !== '');
  return visible.map((part, index) => {
    const key = typeof part === 'string' ? part : (part.code || part.money || index);
    let content = part;
    if (part && typeof part === 'object') {
      content = part.code
        ? <span className="font-mono text-[12px] text-ink-2">{part.code}</span>
        : <b className="font-bold text-ink-2">{part.money}</b>;
    }
    return (
      <span key={`${key}-${index}`}>
        {index ? <span aria-hidden="true"> · </span> : null}
        {index ? <span className="sr-only">, </span> : null}
        {content}
      </span>
    );
  });
}

const subText = (parts) => (parts || [])
  .filter((part) => part !== null && part !== undefined && part !== '')
  .map((part) => (typeof part === 'string' ? part : part.code || part.money))
  .join(' · ');

function NeedsRow({ item }) {
  const age = item.age === null || item.age === undefined ? null : formatDays(item.age);
  return (
    <li
      className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1.5 bg-surface px-4 py-3 max-sm:rounded-[14px] max-sm:border max-sm:border-line max-sm:shadow-card sm:min-h-[60px] sm:grid-cols-[104px_minmax(0,1fr)_64px_112px] sm:items-center sm:px-[18px] sm:hover:bg-surface-hover"
      data-testid="needs-row"
      data-category={item.category}
    >
      <div className="col-start-1 row-start-1 sm:col-start-auto sm:row-start-auto">
        <StatusPill tone={item.tone} label={item.status} size="sm" />
      </div>
      <div className="col-span-2 min-w-0 sm:col-span-1">
        <p className="text-[14px] font-bold leading-snug text-ink sm:truncate sm:text-[13.5px]" title={[item.code, item.title].filter(Boolean).join(' ')}>
          {item.code ? <span className="font-mono text-[12.5px] font-semibold">{item.code} </span> : null}
          {item.title}
        </p>
        <p className="text-[12.5px] leading-snug text-ink-3 sm:truncate" title={subText(item.sub)}>
          <SubParts parts={item.sub} />
        </p>
      </div>
      <div
        className={cn(
          'col-start-2 row-start-1 text-right text-[13px] font-semibold tabular-nums sm:col-start-auto sm:row-start-auto',
          age ? (TONE_TEXT_CLASS[item.ageTone] || 'text-ink-2') : 'text-ink-4'
        )}
        title={age ? `${formatNumber(item.age, { decimals: 0 })} days` : 'No date recorded'}
      >
        {age || (
          <>
            <span aria-hidden="true">—</span>
            <span className="sr-only">No date recorded</span>
          </>
        )}
      </div>
      <div className="col-span-2 sm:col-span-1">
        <Button asChild variant="outline" size="sm" className="h-11 w-full text-[14px] sm:h-[30px] sm:text-[13px]">
          <Link
            to={item.action.to}
            aria-label={item.context ? `${item.action.label}: ${item.context}` : undefined}
            data-testid="needs-row-action"
          >
            {item.action.label}
          </Link>
        </Button>
      </div>
    </li>
  );
}

/**
 * "Needs you today" — the ranked inbox of the dashboard (02 #3), with a tab per kind of work.
 * Controlled tab (`category`, `onCategoryChange`) so the Awaiting approval KPI can open the Approvals tab.
 */
export const NeedsCard = forwardRef(({ items, data, category, onCategoryChange, isPhone = false }, ref) => {
  const [expanded, setExpanded] = useState(false);
  const categories = useMemo(() => availableCategories(data), [data]);
  const counts = useMemo(() => countByCategory(items), [items]);
  const limit = isPhone ? PHONE_ROWS : DESKTOP_ROWS;
  const current = category === 'all' || categories.some((entry) => entry.key === category) ? category : 'all';
  const inView = current === 'all' ? items : items.filter((item) => item.category === current);
  const short = current === 'all' ? pickAllView(items, limit) : inView.slice(0, limit);
  const rows = expanded ? inView : short;
  const hiddenCount = inView.length - short.length;
  const summary = current === 'all' && !expanded ? hiddenSummary(items, short) : '';
  const mine = data?.scope === 'mine';
  const empty = inView.length === 0 ? emptyCategoryText(current, { mine }) : null;

  const changeCategory = (next) => {
    setExpanded(false);
    onCategoryChange(next);
  };

  // Every kind the role can open keeps its tab, 0 included ("Supply 0" = nothing late, not "not measured").
  // An empty tab — also the Approvals tab the Awaiting approval KPI opens when it reads 0 — says why it is
  // empty instead of showing a blank card.
  const options = [
    { key: 'all', label: 'All', count: items.length, testId: 'needs-tab-all' },
    ...categories.map((entry) => ({ key: entry.key, label: entry.label, count: counts[entry.key] || 0, testId: `needs-tab-${entry.key}` })),
  ];

  return (
    <section
      ref={ref}
      tabIndex={-1}
      aria-labelledby="needs-title"
      className="min-w-0 scroll-mt-24 focus:outline-none sm:overflow-hidden sm:rounded-card sm:border sm:border-line sm:bg-surface sm:shadow-card"
      data-testid="needs-card"
    >
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-2.5 pb-2.5 sm:border-b sm:border-line-2 sm:px-[18px] sm:py-3">
        <div className="flex items-center gap-2">
          <h2 id="needs-title" className="text-[16px] font-bold text-ink sm:text-[15px]">Needs you today</h2>
          <span className="rounded-full bg-navy px-2 py-[3px] text-[11.5px] font-bold leading-none tabular-nums text-white" data-testid="needs-count">
            {formatNumber(items.length, { decimals: 0 })}
          </span>
        </div>
        {items.length || current !== 'all' ? (
          <div className="-mx-4 w-[calc(100%+2rem)] overflow-x-auto px-4 sm:mx-0 sm:w-auto sm:px-0">
            <Segmented
              options={options}
              value={current}
              onChange={changeCategory}
              ariaLabel="Show"
              size="sm"
              testId="needs-tabs"
              className="max-sm:[&>button]:h-10 max-sm:[&>button]:px-3.5 max-sm:[&>button]:text-[13px]"
            />
          </div>
        ) : null}
      </div>

      {items.length === 0 && current === 'all' ? (
        <div className="max-sm:rounded-[14px] max-sm:border max-sm:border-line max-sm:bg-surface">
          <EmptyState
            layout="inline"
            tone="green"
            icon={CircleCheck}
            title="Nothing needs you right now"
            text="Late deliveries, stages awaiting approval, blocked drawings and unpaid invoices show up here."
            titleAs="h3"
            className="px-[18px] py-5"
          />
        </div>
      ) : empty ? (
        <div className="max-sm:rounded-[14px] max-sm:border max-sm:border-line max-sm:bg-surface">
          <EmptyState
            layout="inline"
            tone="green"
            icon={CircleCheck}
            title={empty.title}
            text={empty.text}
            titleAs="h3"
            className="px-[18px] py-5"
            data-testid="needs-empty-tab"
            action={(
              <Button type="button" variant="outline" size="sm" onClick={() => changeCategory('all')}>
                Show all{items.length ? ` ${formatNumber(items.length, { decimals: 0 })}` : ''}
              </Button>
            )}
          />
        </div>
      ) : (
        <>
          <ul className="max-sm:space-y-2.5 sm:divide-y sm:divide-[color:var(--line-2)]" data-testid="needs-list">
            {rows.map((item) => <NeedsRow key={item.key} item={item} />)}
          </ul>
          {hiddenCount > 0 || expanded ? (
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 pt-2 sm:border-t sm:border-line-2 sm:px-[18px] sm:py-2.5">
              {summary ? (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="hidden min-w-0 truncate text-left text-[13px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:block"
                  data-testid="needs-hidden-summary"
                >
                  {summary}
                </button>
              ) : <span className="hidden sm:block" />}
              <button
                type="button"
                onClick={() => setExpanded((value) => !value)}
                aria-expanded={expanded}
                className="inline-flex h-11 w-full items-center justify-center gap-1.5 rounded-control text-[14px] font-bold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:h-auto sm:w-auto sm:text-[13px] sm:font-semibold"
                data-testid="needs-toggle"
              >
                {expanded ? 'Show fewer' : (
                  <>
                    {isPhone ? `Show ${hiddenCount} more` : `View all ${formatNumber(inView.length, { decimals: 0 })}`}
                    {isPhone ? <ChevronDown className="h-4 w-4" aria-hidden="true" /> : <ArrowRight className="h-3.5 w-3.5" aria-hidden="true" />}
                  </>
                )}
              </button>
            </div>
          ) : null}
        </>
      )}
    </section>
  );
});
NeedsCard.displayName = 'NeedsCard';
